import type { ReactNode } from 'react';
import { ThemeToggle } from '@/components/synapse/ThemeToggle';
import { Logo } from '@/components/synapse/Logo';
import { cn } from '@/lib/utils';

interface AuthScreenProps {
  /** Optional left-side brand/marketing panel (desktop only). */
  brand?: ReactNode;
  /** Form content. Centered in the right pane (or whole viewport on mobile). */
  children: ReactNode;
  /** Mobile-only footer (e.g. version line). */
  mobileFooter?: ReactNode;
  /** Extra class for the right pane wrapper. */
  rightClassName?: string;
}

/**
 * The full-bleed two-column layout used by login + register. Mobile collapses
 * to a single column with the logo at the top + the form vertically centered.
 * The theme toggle floats in the top-right on both layouts.
 */
export function AuthScreen({ brand, children, mobileFooter, rightClassName }: AuthScreenProps) {
  return (
    <div className="min-h-screen w-full bg-background text-foreground md:flex">
      {brand && <div className="hidden md:flex">{brand}</div>}

      <div
        className={cn(
          'relative flex min-h-screen flex-1 flex-col px-6 pt-10 pb-6 md:items-center md:justify-center md:px-10',
          rightClassName,
        )}
      >
        <div className="pointer-events-none absolute right-4 top-4 z-10 flex items-center gap-2 md:right-6 md:top-6">
          <div className="pointer-events-auto">
            <ThemeToggle />
          </div>
        </div>
        {/* Mobile-only logo at top */}
        <div className="md:hidden">
          <Logo size={26} />
        </div>
        <div className="flex flex-1 flex-col justify-center md:flex-none md:w-full md:max-w-[440px]">
          {children}
        </div>
        {mobileFooter && (
          <div className="text-center text-[11px] text-muted-2 md:hidden">{mobileFooter}</div>
        )}
      </div>
    </div>
  );
}
